import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowDown } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Reveal";
import { MASSAGES, IMAGES } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";
import { DotText } from "@/components/site/DotText";

function MassageRow({ m, index }) {
  const reversed = index % 2 === 1;
  return (
    <motion.article
      data-testid={`massage-item-${index}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className="grid md:grid-cols-2 gap-8 md:gap-14 items-center"
    >
      <Link
        to={`/massages/${m.slug}`}
        data-testid={`massage-image-link-${index}`}
        aria-label={`Découvrir ${m.name}`}
        className={`group block overflow-hidden rounded-md ${reversed ? "md:order-2" : ""}`}
      >
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={m.image}
            alt={`${m.name} à Toulouse — Ninaa Beauty`}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
          />
        </div>
      </Link>

      <div className={reversed ? "md:order-1" : ""}>
        <Link to={`/massages/${m.slug}`} className="inline-block">
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#4b2018] leading-tight hover:text-[#b07a63] transition-colors">
            {m.name}
          </h2>
        </Link>
        {m.slug === "massage-femme-enceinte" && (
          <span
            data-testid={`pregnancy-badge-${index}`}
            className="mt-3 inline-flex items-center rounded-full bg-[#f6f1e9] border border-[#e4dccd] px-3 py-1 text-[11px] tracking-wide text-[#944639]"
          >
            À partir du 2ᵉ trimestre de grossesse
          </span>
        )}
        <p className="mt-4 text-[#4b2018] leading-relaxed max-w-md">
          {m.excerpt}
        </p>
        {m.meta && (
          <p
            data-testid={`massage-meta-${index}`}
            className="mt-3 text-sm text-[#944639]"
          >
            <DotText text={m.meta} />
          </p>
        )}
        {!m.meta && m.slug === "soin-rebozo" && m.price && (
          <p
            data-testid={`massage-price-${index}`}
            className="mt-3 text-sm text-[#944639]"
          >
            {m.price}
          </p>
        )}
        <Link
          to={`/massages/${m.slug}`}
          data-testid={`discover-link-${index}`}
          className="group mt-6 inline-flex items-center gap-2 text-sm tracking-wide text-[#4b2018] hover:text-[#b07a63] transition-colors"
        >
          {m.slug === "soin-rebozo" ? "Découvrir ce soin" : "Découvrir ce massage"}
          <ArrowRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </Link>
      </div>
    </motion.article>
  );
}

export default function Massages() {
  const imgRef = React.useRef(null);
  const [bannerLoaded, setBannerLoaded] = React.useState(false);

  useSeo({
    title: "Massages bien-être à Toulouse — Ninaa Beauty",
    description:
      "Massage relaxant, massage femme enceinte, massage holistique et soin sonore : des massages bien-être sur mesure à Toulouse centre, près de Jean-Jaurès et Matabiau.",
    path: "/massages",
  });

  React.useEffect(() => {
    if (imgRef.current && imgRef.current.complete) setBannerLoaded(true);
  }, []);

  return (
    <div className="App" data-testid="massages-page">
      <Header />
      <main className="page-enter">
        {/* Fine bande de retour, entre le menu et la photo */}
        <div className="bg-white border-b border-[#f0eae0]">
          <div className="max-w-5xl mx-auto px-6 py-2.5">
            <Link
              to="/#prestations"
              data-testid="massages-hero-back"
              className="group inline-flex items-center gap-2 text-sm tracking-wide text-[#4b2018] hover:text-[#b07a63] transition-colors"
            >
              <ArrowLeft
                size={15}
                className="transition-transform duration-300 group-hover:-translate-x-1"
              />
              Retour aux prestations
            </Link>
          </div>
        </div>

        {/* Bannière */}
        <section data-testid="massages-hero" className="relative">
          <div
            className={`relative h-[58vh] min-h-[420px] w-full overflow-hidden transition-[opacity,transform] duration-[1400ms] ease-out ${
              bannerLoaded
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2"
            }`}
          >
            <img
              ref={imgRef}
              src={IMAGES.massagesBanner}
              alt="Massages bien-être à Toulouse — Ninaa Beauty"
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
              onLoad={() => setBannerLoaded(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2b2a27]/55 via-[#2b2a27]/15 to-[#2b2a27]/25" />
            <div className="relative z-10 h-full max-w-5xl mx-auto px-6 flex flex-col items-center justify-center text-center">
              <p className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-white/85 mb-5">
                Ninaa Beauty
              </p>
              <h1 className="font-serif-display font-light leading-[0.95] text-white text-5xl sm:text-6xl md:text-7xl">
                Massages
              </h1>
              <p className="mt-5 text-white/90 text-base md:text-lg leading-relaxed max-w-lg">
                Offrir au corps une parenthèse qui lui appartient…
              </p>
              <p className="mt-4 text-white/70 text-xs md:text-sm max-w-md leading-relaxed">
                Tous les massages proposés sont des massages de bien-être, sans
                visée thérapeutique ni médicale.
              </p>
              <button
                type="button"
                data-testid="massages-scroll-down"
                aria-label="Découvrir les massages"
                onClick={() =>
                  document
                    .querySelector('[data-testid="massage-item-0"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="mt-8 text-white/80 hover:text-white transition-colors animate-bounce"
              >
                <ArrowDown size={22} strokeWidth={1.25} />
              </button>
            </div>
          </div>
        </section>

        {/* Respiration — phrase discrète */}
        <Reveal className="max-w-2xl mx-auto px-6 pt-12 md:pt-16 pb-0 text-center">
          <p className="font-serif-display font-light text-lg md:text-xl leading-relaxed text-[#944639]">
            Prendre du temps pour soi n&apos;est jamais du temps perdu. C&apos;est parfois
            simplement le chemin pour revenir à soi.
          </p>
        </Reveal>

        {/* Liste des massages */}
        <section data-testid="massages-list" className="bg-white">
          <div className="max-w-5xl mx-auto px-6 pt-12 md:pt-16 pb-20 md:pb-24 flex flex-col gap-16 md:gap-20">
            {MASSAGES.map((m, i) => (
              <MassageRow key={m.name} m={m} index={i} />
            ))}
            <Link
              to="/#prestations"
              data-testid="massages-back-to-prestations"
              className="group inline-flex items-center gap-2 text-sm tracking-wide text-[#4b2018] hover:text-[#b07a63] transition-colors"
            >
              <ArrowLeft
                size={16}
                className="transition-transform duration-300 group-hover:-translate-x-1"
              />
              Toutes les prestations
            </Link>
          </div>
        </section>
      </main>
      <Contact />
    </div>
  );
}
